// v2.2 — PWA app mode + all existing features
import { Suspense } from "react";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useStructuredData } from "./hooks/useStructuredData";
import { useUTM } from "./hooks/useUTM";
import { useVisitorTracking } from "./hooks/useVisitorTracking";
import { useAppMode } from "./hooks/useAppMode";
import { lazyRetry } from "./lib/lazyRetry";

const StickyPhoneBar = lazyRetry(() => import("./components/StickyPhoneBar"));
const AppShell = lazyRetry(() => import("./components/app/AppShell"));
const Home = lazyRetry(() => import("./pages/Home"));
const CookieConsent = lazyRetry(() => import("./components/CookieConsent"));
const GrandDesignsStrip = lazyRetry(() => import("./components/GrandDesignsStrip"));

// Lazy-load chat widget to keep initial bundle lean
const ChatWidget = lazyRetry(() => import("./components/ChatWidget"));

// Lazy-load heavy UI providers to keep initial bundle lean
const Toaster = lazyRetry(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const TooltipProvider = lazyRetry(() => import("@/components/ui/tooltip").then(m => ({ default: m.TooltipProvider })));

// All other pages are lazy-loaded to reduce initial JS bundle
const Services = lazyRetry(() => import("./pages/Services"));
const RecentWork = lazyRetry(() => import("./pages/RecentWork"));
const Contact = lazyRetry(() => import("./pages/Contact"));
const AboutUs = lazyRetry(() => import("./pages/AboutUs"));
const HowItWorks = lazyRetry(() => import("./pages/HowItWorks"));
const WhyUs = lazyRetry(() => import("./pages/WhyUs"));
const AreasWeCover = lazyRetry(() => import("./pages/AreasWeCover"));
const AreaPage = lazyRetry(() => import("./pages/AreaPage"));
const RefundPolicy = lazyRetry(() => import("./pages/RefundPolicy"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const AIVisualiser = lazyRetry(() => import("./pages/AIVisualiser"));
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyRetry(() => import("./pages/TermsOfService"));
const FAQs = lazyRetry(() => import("./pages/FAQs"));
const Pricing = lazyRetry(() => import("./pages/Pricing"));
const Showrooms = lazyRetry(() => import("./pages/Showrooms"));
const Brochure = lazyRetry(() => import("./pages/Brochure"));
const Handles = lazyRetry(() => import("./pages/Handles"));
const CustomerStories = lazyRetry(() => import("./pages/CustomerStories"));
const Admin = lazyRetry(() => import("./pages/Admin"));
const VirtualCall = lazyRetry(() => import("./pages/VirtualCall"));
const AdminAnalytics = lazyRetry(() => import("./pages/AdminAnalytics"));
const AdminCMS = lazyRetry(() => import("./pages/AdminCMS"));
const AdminLeadSources = lazyRetry(() => import("./pages/AdminLeadSources"));
const AdminBlog = lazyRetry(() => import("./pages/AdminBlog"));
const BlogListing = lazyRetry(() => import("./pages/blog/BlogListing"));
const BlogArticlePage = lazyRetry(() => import("./pages/blog/BlogArticlePage"));
const DynamicBlogPost = lazyRetry(() => import("./pages/blog/DynamicBlogPost"));
const CommercialServices = lazyRetry(() => import("./pages/CommercialServices"));
const Partners = lazyRetry(() => import("./pages/Partners"));
const Careers = lazyRetry(() => import("./pages/Careers"));
const Press = lazyRetry(() => import("./pages/Press"));
const AnswersMaterials = lazyRetry(() => import("./pages/AnswersMaterials"));
const AnswerPage = lazyRetry(() => import("./pages/AnswerPage"));
const CityLandingPage = lazyRetry(() => import("./pages/CityLandingPage"));
const Links = lazyRetry(() => import("./pages/Links"));
const Finance = lazyRetry(() => import("./pages/Finance"));
const DesignerPricingTool = lazyRetry(() => import("./pages/DesignerPricingTool"));
const EnquiriesDashboard = lazyRetry(() => import("./pages/EnquiriesDashboard"));
const DesignTool = lazyRetry(() => import("./pages/DesignTool"));
const ECatalogue = lazyRetry(() => import("./pages/ECatalogue"));
const Gallery = lazyRetry(() => import("./pages/Gallery"));
const Fireplaces = lazyRetry(() => import("./pages/Fireplaces"));
const You = lazyRetry(() => import("./pages/You"));
const DesignerCall = lazyRetry(() => import("./pages/DesignerCall"));
const ElectricFireplaces = lazyRetry(() => import("./pages/ElectricFireplaces"));
const GrandDesigns2026 = lazyRetry(() => import("./pages/GrandDesigns2026"));
const GrandDesigns2026Terms = lazyRetry(() => import("./pages/GrandDesigns2026Terms"));
const SignContract = lazyRetry(() => import("./pages/SignContract"));
const CustomerPortal = lazyRetry(() => import("./pages/CustomerPortal"));
const GrandDesigns2026Confirmation = lazyRetry(() => import("./pages/GrandDesigns2026Confirmation"));
const RealFireplaces = lazyRetry(() => import("./pages/RealFireplaces"));
const DigitalCard = lazyRetry(() => import("./pages/DigitalCard"));
const ServiceRedirect = lazyRetry(() => import("./components/ServiceRedirect"));
const ComparisonRedirect = lazyRetry(() => import("./components/ComparisonRedirect"));
const CMSDynamicPage = lazyRetry(() => import("./pages/CMSDynamicPage"));
const FullHouseTransformation = lazyRetry(() => import("./pages/FullHouseTransformation"));

// Individual service pages — each has unique H1, meta description, and focused content
const FittedWardrobesPage = lazyRetry(() => import("./pages/services/FittedWardrobesPage"));
const FittedBedroomsPage = lazyRetry(() => import("./pages/services/FittedBedroomsPage"));
const SlidingWardrobesPage = lazyRetry(() => import("./pages/services/SlidingWardrobesPage"));
const WalkInWardrobesPage = lazyRetry(() => import("./pages/services/WalkInWardrobesPage"));
const LoftWardrobesPage = lazyRetry(() => import("./pages/services/LoftWardrobesPage"));
const MediaWallUnitsPage = lazyRetry(() => import("./pages/services/MediaWallUnitsPage"));
const HomeOfficePage = lazyRetry(() => import("./pages/services/HomeOfficePage"));
const UnderStairsPage = lazyRetry(() => import("./pages/services/UnderStairsPage"));
const FittedKitchensPage = lazyRetry(() => import("./pages/services/FittedKitchensPage"));
const SuperLuxuryPage = lazyRetry(() => import("./pages/services/SuperLuxuryPage"));
const KitchenWorktops = lazyRetry(() => import("./pages/KitchenWorktops"));
const QuartzWorktops = lazyRetry(() => import("./pages/worktops/QuartzWorktops"));
const GraniteWorktops = lazyRetry(() => import("./pages/worktops/GraniteWorktops"));
const MarbleWorktops = lazyRetry(() => import("./pages/worktops/MarbleWorktops"));
const PorcelainWorktops = lazyRetry(() => import("./pages/worktops/PorcelainWorktops"));
const QuartziteWorktops = lazyRetry(() => import("./pages/worktops/QuartziteWorktops"));
const DektonWorktops = lazyRetry(() => import("./pages/worktops/DektonWorktops"));

// Minimal loading fallback — invisible to avoid layout shift
const PageLoader = () => <div className="min-h-screen" aria-hidden="true" />;

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        {/* Primary routes */}
        <Route path="/" component={Home} />
        <Route path="/you" component={You} />
        <Route path="/you/" component={You} />
        <Route path="/designer-call" component={DesignerCall} />
        <Route path="/designer-call/" component={DesignerCall} />

        {/* === FULL HOUSE TRANSFORMATION === */}
        <Route path="/full-house" component={FullHouseTransformation} />
        <Route path="/full-house/" component={FullHouseTransformation} />

        {/* === DEDICATED SERVICE PAGES — each URL has unique, focused content === */}
        <Route path="/fitted-wardrobes" component={FittedWardrobesPage} />
        <Route path="/fitted-wardrobes/" component={FittedWardrobesPage} />
        <Route path="/fitted-bedrooms" component={FittedBedroomsPage} />
        <Route path="/fitted-bedrooms/" component={FittedBedroomsPage} />
        <Route path="/sliding-wardrobes" component={SlidingWardrobesPage} />
        <Route path="/sliding-wardrobes/" component={SlidingWardrobesPage} />
        <Route path="/walk-in-wardrobes" component={WalkInWardrobesPage} />
        <Route path="/walk-in-wardrobes/" component={WalkInWardrobesPage} />
        <Route path="/loft-wardrobes" component={LoftWardrobesPage} />
        <Route path="/loft-wardrobes/" component={LoftWardrobesPage} />
        <Route path="/media-wall-units" component={MediaWallUnitsPage} />
        <Route path="/media-wall-units/" component={MediaWallUnitsPage} />
        <Route path="/home-office" component={HomeOfficePage} />
        <Route path="/home-office/" component={HomeOfficePage} />
        <Route path="/under-stairs" component={UnderStairsPage} />
        <Route path="/under-stairs/" component={UnderStairsPage} />
        <Route path="/fitted-kitchens" component={FittedKitchensPage} />
        <Route path="/fitted-kitchens/" component={FittedKitchensPage} />
        <Route path="/super-luxury" component={SuperLuxuryPage} />
        <Route path="/super-luxury/" component={SuperLuxuryPage} />
        <Route path="/kitchen-worktops" component={KitchenWorktops} />
        <Route path="/kitchen-worktops/" component={KitchenWorktops} />
        <Route path="/kitchen-worktops/quartz" component={QuartzWorktops} />
        <Route path="/kitchen-worktops/quartz/" component={QuartzWorktops} />
        <Route path="/kitchen-worktops/granite" component={GraniteWorktops} />
        <Route path="/kitchen-worktops/granite/" component={GraniteWorktops} />
        <Route path="/kitchen-worktops/marble" component={MarbleWorktops} />
        <Route path="/kitchen-worktops/marble/" component={MarbleWorktops} />
        <Route path="/kitchen-worktops/porcelain" component={PorcelainWorktops} />
        <Route path="/kitchen-worktops/porcelain/" component={PorcelainWorktops} />
        <Route path="/kitchen-worktops/quartzite" component={QuartziteWorktops} />
        <Route path="/kitchen-worktops/quartzite/" component={QuartziteWorktops} />
        <Route path="/kitchen-worktops/dekton" component={DektonWorktops} />
        <Route path="/kitchen-worktops/dekton/" component={DektonWorktops} />
        <Route path="/fireplaces" component={Fireplaces} />
        <Route path="/fireplaces/" component={Fireplaces} />
        <Route path="/fireplaces/electric" component={ElectricFireplaces} />
        <Route path="/fireplaces/electric/" component={ElectricFireplaces} />
        <Route path="/fireplaces/real" component={RealFireplaces} />
        <Route path="/fireplaces/real/" component={RealFireplaces} />

        {/* Top-level city SEO landing pages */}
        <Route path="/fitted-wardrobes-london" component={CityLandingPage} />
        <Route path="/fitted-wardrobes-london/" component={CityLandingPage} />
        <Route path="/fitted-wardrobes-brighton" component={CityLandingPage} />
        <Route path="/fitted-wardrobes-brighton/" component={CityLandingPage} />
        <Route path="/fitted-wardrobes-surrey" component={CityLandingPage} />
        <Route path="/fitted-wardrobes-surrey/" component={CityLandingPage} />
        <Route path="/fitted-wardrobes-essex" component={CityLandingPage} />
        <Route path="/fitted-wardrobes-essex/" component={CityLandingPage} />
        <Route path="/fitted-wardrobes-kent" component={CityLandingPage} />
        <Route path="/fitted-wardrobes-kent/" component={CityLandingPage} />

        {/* Services overview page */}
        <Route path="/fitted-furniture" component={Services} />
        <Route path="/fitted-furniture/" component={Services} />
        <Route path="/services" component={Services} />

        {/* About, contact, how-it-works */}
        <Route path="/about" component={AboutUs} />
        <Route path="/about/" component={AboutUs} />
        <Route path="/about-us" component={AboutUs} />
        <Route path="/about-us/" component={AboutUs} />
        <Route path="/contact" component={Contact} />
        <Route path="/contact/" component={Contact} />
        <Route path="/arrange-a-free-home-visit" component={Contact} />
        <Route path="/arrange-a-free-home-visit/" component={Contact} />
        <Route path="/book-free-design-visit" component={Contact} />
        <Route path="/book-free-design-visit/" component={Contact} />
        <Route path="/how-it-works" component={HowItWorks} />
        <Route path="/how-it-works/" component={HowItWorks} />
        <Route path="/why-choose-us" component={WhyUs} />
        <Route path="/why-choose-us/" component={WhyUs} />
        <Route path="/why-us" component={WhyUs} />
        <Route path="/why-us/" component={WhyUs} />

        {/* Gallery (photos) */}
        <Route path="/gallery" component={Gallery} />
        <Route path="/gallery/" component={Gallery} />
        <Route path="/recent-work" component={RecentWork} />
        <Route path="/recent-work/" component={RecentWork} />

        {/* Areas */}
        <Route path="/area-we-cover" component={AreasWeCover} />
        <Route path="/area-we-cover/" component={AreasWeCover} />
        <Route path="/areas" component={AreasWeCover} />
        <Route path="/areas/:area" component={AreaPage} />

        {/* AI Visualiser */}
        <Route path="/ai-visualiser" component={AIVisualiser} />
        <Route path="/ai-visualiser/" component={AIVisualiser} />


        {/* Legal pages */}
        <Route path="/refund-policy" component={RefundPolicy} />
        <Route path="/refund-policy/" component={RefundPolicy} />
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/privacy-policy/" component={PrivacyPolicy} />
        <Route path="/terms" component={TermsOfService} />
        <Route path="/terms/" component={TermsOfService} />
        <Route path="/terms-of-service" component={TermsOfService} />
        <Route path="/terms-of-service/" component={TermsOfService} />

        {/* Pricing */}
        <Route path="/pricing" component={Pricing} />
        <Route path="/pricing/" component={Pricing} />

        {/* Finance */}
        <Route path="/finance" component={Finance} />
        <Route path="/finance/" component={Finance} />

        {/* Brochure */}
        <Route path="/brochure" component={Brochure} />
        <Route path="/brochure/" component={Brochure} />
        <Route path="/free-brochure" component={Brochure} />
        <Route path="/free-brochure/" component={Brochure} />
        <Route path="/e-catalogue" component={ECatalogue} />
        <Route path="/e-catalogue/" component={ECatalogue} />

        {/* Handle Catalogue */}
        <Route path="/handles" component={Handles} />
        <Route path="/handles/" component={Handles} />

        {/* Virtual Call */}
        <Route path="/virtual-call" component={VirtualCall} />
        <Route path="/virtual-call/" component={VirtualCall} />

        {/* Showrooms */}
        <Route path="/showrooms" component={Showrooms} />
            <Route path="/customer-stories" component={CustomerStories} />
        <Route path="/showrooms/" component={Showrooms} />

        {/* FAQs */}
        <Route path="/faqs" component={FAQs} />
        <Route path="/faqs/" component={FAQs} />
        <Route path="/faq" component={FAQs} />
        <Route path="/faq/" component={FAQs} />

        {/* Commercial & Partners */}
        <Route path="/commercial-services" component={CommercialServices} />
        <Route path="/commercial-services/" component={CommercialServices} />
        <Route path="/commercial" component={CommercialServices} />
        <Route path="/partners" component={Partners} />
        <Route path="/partners/" component={Partners} />
        <Route path="/trade" component={Partners} />

        {/* Careers / Join Our Team */}
        <Route path="/careers" component={Careers} />
        <Route path="/careers/" component={Careers} />
        <Route path="/join-our-team" component={Careers} />
        <Route path="/join-our-team/" component={Careers} />

        {/* Press & Credentials */}
        <Route path="/press" component={Press} />
        <Route path="/press/" component={Press} />
        <Route path="/credentials" component={Press} />
        <Route path="/credentials/" component={Press} />

        {/* Answers Hub — AI-targeted comparison content */}
        <Route path="/answers/wardrobe-materials" component={AnswersMaterials} />
        <Route path="/answers/wardrobe-materials/" component={AnswersMaterials} />
        <Route path="/answers/how-much-do-fitted-wardrobes-cost">{() => <AnswerPage slug="how-much-do-fitted-wardrobes-cost" />}</Route>
        <Route path="/answers/how-much-do-fitted-wardrobes-cost/">{() => <AnswerPage slug="how-much-do-fitted-wardrobes-cost" />}</Route>
        <Route path="/answers/how-long-do-fitted-wardrobes-take-to-install">{() => <AnswerPage slug="how-long-do-fitted-wardrobes-take-to-install" />}</Route>
        <Route path="/answers/how-long-do-fitted-wardrobes-take-to-install/">{() => <AnswerPage slug="how-long-do-fitted-wardrobes-take-to-install" />}</Route>
        <Route path="/answers/fitted-wardrobes-vs-freestanding">{() => <AnswerPage slug="fitted-wardrobes-vs-freestanding" />}</Route>
        <Route path="/answers/fitted-wardrobes-vs-freestanding/">{() => <AnswerPage slug="fitted-wardrobes-vs-freestanding" />}</Route>
        <Route path="/answers/best-wardrobe-door-styles">{() => <AnswerPage slug="best-wardrobe-door-styles" />}</Route>
        <Route path="/answers/best-wardrobe-door-styles/">{() => <AnswerPage slug="best-wardrobe-door-styles" />}</Route>
        <Route path="/answers/how-to-organise-a-fitted-wardrobe">{() => <AnswerPage slug="how-to-organise-a-fitted-wardrobe" />}</Route>
        <Route path="/answers/how-to-organise-a-fitted-wardrobe/">{() => <AnswerPage slug="how-to-organise-a-fitted-wardrobe" />}</Route>
        <Route path="/answers/can-you-fit-wardrobes-in-a-loft-conversion">{() => <AnswerPage slug="can-you-fit-wardrobes-in-a-loft-conversion" />}</Route>
        <Route path="/answers/can-you-fit-wardrobes-in-a-loft-conversion/">{() => <AnswerPage slug="can-you-fit-wardrobes-in-a-loft-conversion" />}</Route>
        <Route path="/answers/what-is-the-best-colour-for-fitted-wardrobes">{() => <AnswerPage slug="what-is-the-best-colour-for-fitted-wardrobes" />}</Route>
        <Route path="/answers/what-is-the-best-colour-for-fitted-wardrobes/">{() => <AnswerPage slug="what-is-the-best-colour-for-fitted-wardrobes" />}</Route>
        <Route path="/answers/do-fitted-wardrobes-add-value-to-your-home">{() => <AnswerPage slug="do-fitted-wardrobes-add-value-to-your-home" />}</Route>
        <Route path="/answers/do-fitted-wardrobes-add-value-to-your-home/">{() => <AnswerPage slug="do-fitted-wardrobes-add-value-to-your-home" />}</Route>
        <Route path="/answers/what-to-expect-during-a-wardrobe-design-visit">{() => <AnswerPage slug="what-to-expect-during-a-wardrobe-design-visit" />}</Route>
        <Route path="/answers/what-to-expect-during-a-wardrobe-design-visit/">{() => <AnswerPage slug="what-to-expect-during-a-wardrobe-design-visit" />}</Route>
        <Route path="/answers/sliding-vs-hinged-wardrobe-doors">{() => <AnswerPage slug="sliding-vs-hinged-wardrobe-doors" />}</Route>
        <Route path="/answers/sliding-vs-hinged-wardrobe-doors/">{() => <AnswerPage slug="sliding-vs-hinged-wardrobe-doors" />}</Route>
        <Route path="/answers/how-to-measure-for-fitted-wardrobes">{() => <AnswerPage slug="how-to-measure-for-fitted-wardrobes" />}</Route>
        <Route path="/answers/how-to-measure-for-fitted-wardrobes/">{() => <AnswerPage slug="how-to-measure-for-fitted-wardrobes" />}</Route>

        {/* Digital Business Cards — must be before /:parentSlug/:slug catch-all */}
        <Route path="/card/:slug" component={DigitalCard} />
        <Route path="/card/:slug/" component={DigitalCard} />

        {/* Redirect legacy /services/:slug URLs to top-level service pages */}
        <Route path="/services/:slug" component={ServiceRedirect} />

        {/* SEO-friendly comparison page redirects */}
        <Route path="/fitted-wardrobes-vs-sharps" component={ComparisonRedirect} />
        <Route path="/fitted-wardrobes-vs-hammonds" component={ComparisonRedirect} />
        <Route path="/fitted-wardrobes-vs-neville-johnson" component={ComparisonRedirect} />
        <Route path="/fitted-wardrobes-vs-sliderobes" component={ComparisonRedirect} />
        <Route path="/vs-sharps" component={ComparisonRedirect} />
        <Route path="/vs-hammonds" component={ComparisonRedirect} />
        <Route path="/vs-neville-johnson" component={ComparisonRedirect} />
        <Route path="/vs-sliderobes" component={ComparisonRedirect} />
        <Route path="/sharps-alternative" component={ComparisonRedirect} />
        <Route path="/hammonds-alternative" component={ComparisonRedirect} />

        {/* Blog / Design Ideas */}
        <Route path="/blog" component={BlogListing} />
        <Route path="/blog/" component={BlogListing} />
        <Route path="/blog/:slug" component={DynamicBlogPost} />
        <Route path="/design-ideas" component={BlogListing} />
        <Route path="/design-ideas/" component={BlogListing} />
        {/* Admin panel — must be before catch-all */}
        <Route path="/admin/analytics" component={AdminAnalytics} />
        <Route path="/admin/lead-sources" component={AdminLeadSources} />
        <Route path="/admin/pages" component={AdminCMS} />
        <Route path="/admin/blog" component={AdminBlog} />
        <Route path="/admin" component={Admin} />
        <Route path="/:parentSlug/:slug" component={BlogArticlePage} />

        {/* Link in Bio — replaces Linktree */}
        <Route path="/links" component={Links} />
        <Route path="/links/" component={Links} />

        {/* Designer Tool — standalone, no site chrome */}
        <Route path="/designertool" component={DesignerPricingTool} />
        <Route path="/designertool/" component={DesignerPricingTool} />

        {/* Design Tool — customer-facing wardrobe/media wall designer */}
        <Route path="/design-tool" component={DesignTool} />
        <Route path="/design-tool/" component={DesignTool} />

        {/* Grand Designs Live 2026 Campaign */}
        <Route path="/grand-designs-2026" component={GrandDesigns2026} />
        <Route path="/grand-designs-2026/" component={GrandDesigns2026} />
        <Route path="/grand-designs-2026-terms" component={GrandDesigns2026Terms} />
        <Route path="/grand-designs-2026-terms/" component={GrandDesigns2026Terms} />
        <Route path="/grand-designs-2026/confirmation" component={GrandDesigns2026Confirmation} />
        <Route path="/grand-designs-2026/confirmation/" component={GrandDesigns2026Confirmation} />
        {/* Customer Portal — magic link access for customers */}
        <Route path="/portal" component={CustomerPortal} />
        <Route path="/portal/" component={CustomerPortal} />
        {/* E-Sign Contract */}
        <Route path="/sign-contract" component={SignContract} />
        <Route path="/sign-contract/" component={SignContract} />




        {/* Team enquiries dashboard */}
        <Route path="/enquiries" component={EnquiriesDashboard} />
        {/* CMS dynamic pages — catches any slug created via /admin/pages */}
        <Route path="/:slug" component={CMSDynamicPage} />
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  const isAppMode = useAppMode();
  useStructuredData();
  useUTM(); // Capture UTM params from URL on first visit
  useVisitorTracking(); // Track visitor sessions, page views, scroll depth, form interactions

  // Check if this is a card page opened from home screen — show card, not AppShell
  const isCardPage = window.location.pathname.startsWith("/card/");

  // PWA app mode — show the native-like app shell with 5-tab navigation
  // Exception: /card/:slug pages should render as standalone card view
  if (isAppMode && !isCardPage) {
    return (
      <ErrorBoundary>
        <ThemeProvider defaultTheme="dark">
          <Suspense fallback={null}>
            <TooltipProvider>
              <Suspense fallback={null}><Toaster /></Suspense>
              <Suspense fallback={<div className="min-h-screen bg-[#0F0F11]" />}>
                <AppShell />
              </Suspense>
            </TooltipProvider>
          </Suspense>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // Regular website mode
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <Suspense fallback={null}>
          <TooltipProvider>
            <Suspense fallback={null}><Toaster /></Suspense>
            <Suspense fallback={null}><GrandDesignsStrip /></Suspense>
            <main id="main-content">
              <Router />
            </main>
            <Suspense fallback={null}><CookieConsent /></Suspense>
            <Suspense fallback={null}><ChatWidget /></Suspense>
            <Suspense fallback={null}><StickyPhoneBar /></Suspense>
          </TooltipProvider>
        </Suspense>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
